// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import Axios from 'axios';

import BaseService from '@core/services/base';
import { ListRequestParams } from '@shared/types/services';
import { REQUEST_PARAMS } from '@shared/constants/services';
import { BLOCK_FIELDS, TIMELINE_FIELDS, timelineTestDtos } from '@shared/constants/timeline-data';
import { BlockOption } from '@shared/models/jobrequesttimelines/lineblock';
import { Id } from '@shared/types/common';


import { TimelineData, TimelineDataDTO } from '@shared/models/jobrequesttimelines/timeline-data';
import { LineBlockDTO } from '@shared/models/jobrequesttimelines/lineblock';
import { IdsFromFormDTO } from '@shared/models/jobrequesttimelines/lineblock';
import { NewTimelineDTO, FormListDTO } from '@shared/models/jobrequesttimelines/new-timeline';
//
// Main service class to read timeline data from portal-api.
@injectable()
export default class TimelineDataService extends BaseService<TimelineDataDTO, TimelineData>{
  // protected urlPrefix = 'dashboard';  // path to portal-api endpoint.
  static diToken = Symbol('jobrequesttimelines-service');
  static testTimelines = false; // WXYZ
  static testNewForm = false; // WXYZ
  static testAssetsCollections = false;
  constructor() {
    super({ domainArea: 'dashboard', Model: TimelineData });
  }

  // Setup a query structure to retrieve data.
  // portal-api should parse this and response accordingly.
  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        jobRequestTimelines(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { field: ${sorting?.field}, type: ${sorting?.type}},
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
        ) {
          items {
            ${TIMELINE_FIELDS.id},
            ${TIMELINE_FIELDS.title},              
            ${TIMELINE_FIELDS.elements}: {
              ${BLOCK_FIELDS.id},
              ${BLOCK_FIELDS.standing},
              ${BLOCK_FIELDS.start},
              ${BLOCK_FIELDS.end},     
              ${BLOCK_FIELDS.onTimeStatus},       
            }
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  // Query portal-api for asset/collection/projection/pilot/... property's Ids (assetId, pilotId, statusId, ....) for a job request.
  // Input:
  //   jrNameNId: job request Id.
  async getSitePropertiesIds(jrNameNId: BlockOption): Promise<Array<BlockOption>> {
    try {
      const { id } = jrNameNId;
      const url = this.getURL(`jobrequestgetids/${id}`);
      let data: Array<BlockOption>;
      if (!TimelineDataService.testAssetsCollections)
      {
        const dataFromRest  = await Axios.get(url);
        //data = dataFromRest as unknown as Array<Array<{id: Id, name: string}>>; // ???
        data = dataFromRest.data;
      }
      else
      {
        data = [{id: 18, type: 'asset', label: 'test'}, {id: 11, type: 'collection', label: 'test2'}]; // This test case needs to be updated or removed.
      }

      return data;
    } 
    catch (err) {
      throw err;  // TBD: may want to handle this.
    }
  }

  // Get a list of job request lines from the portal-api.
  // To follow the existing data structure, we insert the orderId at 'ListRequestParams.searchKeyword' (kind of kludgy).
  async getList(params: ListRequestParams): Promise<{ pageInfo: {totalCount: number;
                                                                  hasPreviousPage: boolean;
                                                                  hasNextPage: boolean;
                                                                },
                                                      items: TimelineData[] }> {
    try {        
      const url = this.getURL('timelines');
      let data;
      if (TimelineDataService.testTimelines == false)
      {
        const dateRange = params.filtering?.dateRange !== undefined ? params.filtering?.dateRange : [];
        const filterBy = params.filtering?.byCategory !== undefined ? params.filtering?.byCategory : undefined;
        const dataFromRest  = await Axios.post(
          url, 
          JSON.stringify({ dateRange: dateRange, filterBy: filterBy }), 
          { headers: { 'Content-Type': 'application/json' } }
        );

        // Normalize the data into the common format for a getlist response.
        data = {
          jobRequestTimelines: {
            items: dataFromRest.data,
            pageInfo: {
              totalCount: 1,
              hasPreviousPage: false,
              hasNextPage: false,
            }
          }
        };
      }
      else
      {
        data = {
          jobRequestTimelines: {
            items: timelineTestDtos,
            pageInfo: {
              totalCount: 1,
              hasPreviousPage: false,
              hasNextPage: false,
            }
          }
        };
      }

      // Destructuring info from the response.
      const {
        jobRequestTimelines: { items, pageInfo },
      } = data;
  
      // Return reponse to caller.
      return {
        pageInfo,
        items: items.map((dto) => new TimelineData(dto)),
      };
    } 
    catch (err) {
      throw err;  // TBD: may want to handle this here.
    }
  }

  // Send request to update a timeline to portal-api.
  // Returns a timelinedata obj which will be used to update the chart.
  async updateJRTimeline(jobRequestId: number | string, timelineBlk: Partial<LineBlockDTO>, ids: IdsFromFormDTO | undefined): Promise<TimelineData | undefined> {

    const url = this.getURL(`job-requests/${jobRequestId}`);
    // if there are ids, merge them with the block data.
    const payload = ids ? {...timelineBlk, ...ids} : timelineBlk;
    // console.log("**** in service send updateJRTimeline:", payload);

    try {      
      if (TimelineDataService.testTimelines == false) {
        const { data } = await Axios.put(url, payload);

        if(data === undefined) {
          return undefined;
        }

        return new TimelineData(data);
      }
      else {
        return new TimelineData(timelineTestDtos[0]);
      }
    }
    catch (err) {
      throw err;  // TBD: may want to handle this instead of rethrow.
    }
  }

  // Send request to add a new timeline to portal-api.
  // Returns a timelinedata obj which will be used to update the chart.
  async addJRTimeline(newTimeline: NewTimelineDTO): Promise<TimelineData | undefined> {

    const url = this.getURL(`timelines/new`);

    try {
      
      if (TimelineDataService.testTimelines == false) {
        const { data } = await Axios.post(url, newTimeline);

        if(data === undefined) {
          return undefined;
        }

        return new TimelineData(data);
      }
      else {
        return new TimelineData(timelineTestDtos[0]);
      }
    }
    catch (err) {
      // WXYZ throw err;  // TBD: may want to handle this instead of rethrow.
      console.log(err)
    }
    return undefined;
  }

  // Get the lists for customers/orders/assets to display in the NewTimeline form.
  // Input:
  //   queryData: array of customerID/label, orderID/label, assetID/label.
  // Return:
  //   array of customer, order, and asset arrays.
  // async getListsForNewJRForm(query: FormListDTO[]) {
  async getListsForNewJRForm(query: FormListDTO[]): Promise<Array<Array<{id: Id, name: string}>>> {
    try {
      const url = this.getURL('timelines/lists');
      let data: Array<Array<{id: Id, name: string}>>;
      if (TimelineDataService.testNewForm == false)
      {
        const dataFromRest  = await Axios.post(url, query);
        //data = dataFromRest as unknown as Array<Array<{id: Id, name: string}>>; // ???
        data = dataFromRest.data;
      }
      else
      {
        // This sample data is probably no longer valid.
        data = [
          [ 
            {id: 1, name: 'Customer 1'},
            {id: 2, name: 'Customer 2'},
            {id: 3, name: 'Z Customer 3'},
          ],
          [
            {id: 1, name: 'A Order 1'},
            {id: 2, name: 'Order 2'},
            {id: 3, name: 'A Order 3'},
          ],
          [
            {id: 1, name: 'Asset 1'},
            {id: 2, name: 'B Asset 2'},
            {id: 3, name: 'B Asset 3'},
          ],
        ];
      }

      return data;
    } 
    catch (err) {
      throw err;  // TBD: may want to handle this here.
    }
  }

  // Build endpoint from prefix and Id.
  // getURL(path: Id) {
  //   return `${this.urlPrefix}/${path}`;
  // }
}
